import { cloneDeep, isEqual, throttle } from "lodash-es";

/**
 * 很多时候，我们需要开发一个 Editor 组件，但是他的输入/输出是一个 Object 或者 Array。
 *
 * 如果直接在传入的 prop 上面修改，可能会导致一些奇怪的联动问题。
 *
 * 为解决这个问题，一般会 **复制一个** 临时的副本，在副本上修改，最后再提交出去。
 * 但是这种复制的逻辑特别繁琐，尤其遇到 debounce 或者 throttle 的时候。
 *
 * 这里提供了一个 mixin 封装了这种繁琐的逻辑。下面是一个例子
 *
 * ```js
 * export default {
 *   model: {
 *     prop: 'value',
 *     event: 'change',
 *   },
 *   props: {
 *     value: { type: Object }
 *   },
 *   mixins: [
 *     vueObjectEditorMixin({
 *       // 这里可以配置一些选项，比如 throttleTimeout 之类的
 *       tempVarName: 'tempValue',
 *     })
 *   ]
 * }
 * ```
 *
 * 然后就可以对 `this.tempValue` 随便瞎操作了，当内部发生修改的时候，这个组件会自动通过事件提交；外部传入修改的时候，会自动更新
 */
export const vueObjectEditorMixin = ({
  propName = 'value',
  eventName = 'change',
  tempVarName = 'tempValue',
  flushMethodName = '',
  throttleTimeout = 500,
  // lodash默认就是true
  leading = true,
  throttleImpl = throttle,
  // 是否保持原有的引用
  keepRef = false
}) => {
  const commit = `$_commit:${propName}`;
  const haltTimeout = Math.min(100, throttleTimeout >>> 1);
  const clone = keepRef ? item => item : cloneDeep;
  const ans = {
    data() {
      return {
        [tempVarName]: clone(this.$props[propName])
      };
    },
    // mixin 进来的 created 钩子执行顺序比原来组件的靠前，但是仍然晚于 provide()
    created() {
      let noSyncFromOutsideUntil = 0;
      this[commit] = throttleImpl(() => {
        noSyncFromOutsideUntil = Date.now() + haltTimeout;
        this.$emit(eventName, clone(this[tempVarName]));
      }, throttleTimeout, {
        leading
      });
      this.$watch(() => this[propName], newOutValue => {
        /**
         * 如果这个外部带来的变化，是组件自己的修改导致的，那么就忽略这次外部传入的修改
         * 需要考虑时间的因素，避免误伤
         */
        if (noSyncFromOutsideUntil !== 0 && noSyncFromOutsideUntil > Date.now()) {
          noSyncFromOutsideUntil = 0;
          return;
        }

        /**
         * 排除了循环触发的因素后，再用 isEqual 做一次排除
         *
         * 注意，这里修改了 [tempVarName]，会导致 this[commit] 这个 debouncedFn 被调用，因此还得 cancel 一下
         */
        if (!isEqual(this[tempVarName], newOutValue)) {
          this[tempVarName] = clone(newOutValue);
          this.$nextTick(() => this[commit].cancel());
        }
      }, {
        immediate: true,
        deep: true
      });

      // 这时候 被mixin的created还没有执行 所以无需commit出去 因为此时temp的值一定是propName的值
      this.$watch(() => this[tempVarName], this[commit], {
        deep: true
      });
    },
    beforeDestroy() {
      this[commit].flush();
    },
    methods: {}
  };
  if (flushMethodName) {
    ans.methods[flushMethodName] = function () {
      this[commit].flush();
    };
  }
  return ans;
};
//# sourceMappingURL=vueObjectEditorMixin.js.map
